import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { TenantAuthClaims } from '@models';
import { Observable, catchError, first, from, of, switchMap } from 'rxjs';
import { TenantContextService } from '@services';

/**
 * Blocks MANAGER role from accessing federation-admin routes.
 *
 * MANAGERs are club delegates within a federation — they must only access
 * /dashboard/club. If they navigate directly to any other dashboard route,
 * this guard redirects them to /dashboard/club.
 *
 * Apply as `canActivateChild` on the wrapper route that groups all
 * federation-admin children (everything except /dashboard/club).
 */
export function federationOnlyGuard(): CanActivateChildFn {
  return (
    _childRoute: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> => {
    const auth = inject(Auth);
    const router = inject(Router);
    const tenantContext = inject(TenantContextService);

    return from(auth.authStateReady()).pipe(
      switchMap(() => authState(auth).pipe(first())),
      catchError(() => of(null)),
      switchMap((user) => {
        if (!user) return of(router.createUrlTree(['/login']));

        return from(user.getIdTokenResult()).pipe(
          switchMap((tokenResult) => {
            const claims = tokenResult.claims as unknown as TenantAuthClaims;

            // Super-admins are not bound to a single tenant role
            if (claims?.superAdmin === true) return of(true);

            // Resolve role from the active tenant (respects switcher state)
            let activeTenantId: string | null = null;
            try {
              activeTenantId = tenantContext.tenantId;
            } catch {
              activeTenantId = claims?.tenants
                ? (Object.keys(claims.tenants)[0] ?? null)
                : null;
            }

            const userRole =
              activeTenantId && claims?.tenants?.[activeTenantId]
                ? claims.tenants[activeTenantId]
                : undefined;

            if (userRole === 'MANAGER') {
              // Managers should only see /dashboard/club — redirect them there
              return of(router.createUrlTree(['/dashboard/club']));
            }

            return of(true);
          }),
          catchError(() => of(router.createUrlTree(['/login']))),
        );
      }),
    );
  };
}
